import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileSearchOutlined, ReloadOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import {
    getSalesDocumentListAPI,
    getSalesDocumentRuntimeEnvironmentAPI,
    getOrganizationRefactoringReasonsAPI,
    getDocumentRefactoringPreviewAPI,
    postDocumentRefactoringAPI
} from '../store/user/restCallUser';
import {
    ProfileResponse,
    OrganizationRefactoringReason,
    DocumentRefactoringPreview,
    RuntimeEnvironmentResponse,
    SalesDocumentListItem,
    SalesDocumentListResponse
} from '../store/user/types';

const { Text } = Typography;

interface SalesDocumentsManagementProps {
    getProfile: any;
}

interface SalesDocumentFilters {
    search?: string;
    status?: string;
    billingMode?: string;
    fromDate?: string;
    toDate?: string;
}

const EMPTY_LIST: SalesDocumentListResponse = {
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 20
};

const getResponseData = (response: any) => {
    const value = response?.Response?.data || response?.Response;
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const getOrganizationId = (profile?: ProfileResponse) => {
    const contract = profile?.contract || profile?.Contract || {};
    return String(contract.organizationId || contract.OrganizationId || profile?.organizationId || '');
};

const formatMoney = (value: number) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatDate = (value: string) => {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('es-EC');
};

const createIdempotencyKey = () => {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
        globalThis.crypto.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
};

const getStatusColor = (status: string) => {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'PAGADO' || normalized === 'ACTIVO') return 'green';
    if (normalized === 'ANULADO' || normalized === 'RECHAZADO') return 'red';
    return 'gold';
};

function SalesDocumentsManagement({ getProfile }: SalesDocumentsManagementProps) {
    const [form] = Form.useForm<SalesDocumentFilters>();
    const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironmentResponse | null>(null);
    const [filters, setFilters] = useState<SalesDocumentFilters>({});
    const [page, setPage] = useState(1);
    const [list, setList] = useState<SalesDocumentListResponse>(EMPTY_LIST);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [organizationId, setOrganizationId] = useState('');
    const [profile, setProfile] = useState<ProfileResponse | null>(null);
    const [selectedDocument, setSelectedDocument] = useState<SalesDocumentListItem | null>(null);
    const [reasons, setReasons] = useState<OrganizationRefactoringReason[]>([]);
    const [selectedReasonId, setSelectedReasonId] = useState<string>();
    const [preview, setPreview] = useState<DocumentRefactoringPreview | null>(null);
    const [paymentDisposition, setPaymentDisposition] = useState<string>();
    const [replacementSalesDocumentId, setReplacementSalesDocumentId] = useState<string>();
    const [observation, setObservation] = useState('');
    const [refactoringLoading, setRefactoringLoading] = useState(false);

    const load = useCallback(async (nextFilters: SalesDocumentFilters = {}, requestedPage = 1) => {
        setLoading(true);
        setError(null);
        try {
            const profile = await getProfile();
            const currentOrganizationId = getOrganizationId(profile);
            if (!currentOrganizationId) throw new Error('No se pudo identificar la organización emisora.');
            setOrganizationId(currentOrganizationId);
            setProfile(profile);
            const [documentsResponse, runtimeResponse] = await Promise.all([
                getSalesDocumentListAPI(currentOrganizationId, {
                    ...nextFilters,
                    page: requestedPage,
                    pageSize: 20
                }),
                getSalesDocumentRuntimeEnvironmentAPI()
            ]);

            if (runtimeResponse.StatusCode >= 200 && runtimeResponse.StatusCode < 300) {
                setRuntimeEnvironment(getResponseData(runtimeResponse) as RuntimeEnvironmentResponse);
            }

            if (documentsResponse.StatusCode < 200 || documentsResponse.StatusCode >= 300) {
                throw new Error(documentsResponse.Error?.message || 'No se pudieron cargar las facturas.');
            }

            const data = getResponseData(documentsResponse);
            setList({
                items: Array.isArray(data?.items) ? data.items : [],
                totalCount: Number(data?.totalCount || 0),
                page: Number(data?.page || requestedPage),
                pageSize: Number(data?.pageSize || 20)
            });
            setPage(Number(data?.page || requestedPage));
        } catch (error: any) {
            setList(EMPTY_LIST);
            setError(error?.message || 'No se pudieron cargar las facturas.');
        } finally {
            setLoading(false);
        }
    }, [getProfile]);

    useEffect(() => {
        void load({}, 1);
    }, [load]);

    const columns = useMemo<ColumnsType<SalesDocumentListItem>>(() => [
        {
            title: 'Factura',
            key: 'number',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.documentSeries}-{record.documentNumber}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(record.issueDate)}</Text>
                </Space>
            )
        },
        {
            title: 'Cliente',
            key: 'client',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.clientName || 'Sin cliente'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.clientIdentificationType} {record.clientIdentificationNumber}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Emisión',
            dataIndex: 'billingMode',
            key: 'billingMode',
            render: (value: string) => <Tag color={value === 'DIRECTA' ? 'blue' : 'purple'}>{value}</Tag>
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            render: (value: number) => <Text strong>{formatMoney(value)}</Text>
        },
        {
            title: 'Pago',
            key: 'payment',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Tag color={getStatusColor(record.paymentStatus)}>{record.paymentStatus}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatMoney(record.paidAmount)}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Button
                    danger
                    size="small"
                    icon={<StopOutlined />}
                    disabled={record.status === 'ANULADO'}
                    onClick={() => void openRefactoring(record)}
                >
                    Anular / refacturar
                </Button>
            )
        }
    ], [organizationId]);

    const openRefactoring = async (document: SalesDocumentListItem) => {
        setSelectedDocument(document);
        setSelectedReasonId(undefined);
        setPreview(null);
        setPaymentDisposition(undefined);
        setReplacementSalesDocumentId(undefined);
        setObservation('');
        setRefactoringLoading(true);
        try {
            const response = await getOrganizationRefactoringReasonsAPI(organizationId);
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudieron cargar los motivos.');
            const data = getResponseData(response);
            setReasons((Array.isArray(data) ? data : []).filter((item: OrganizationRefactoringReason) => item.status === 'ACTIVO'));
        } catch (error: any) {
            message.error(error?.message || 'No se pudieron cargar los motivos.');
            setSelectedDocument(null);
        } finally { setRefactoringLoading(false); }
    };

    const loadPreview = async (reasonId: string) => {
        if (!selectedDocument) return;
        setSelectedReasonId(reasonId);
        setPreview(null);
        setPaymentDisposition(undefined);
        setRefactoringLoading(true);
        try {
            const response = await getDocumentRefactoringPreviewAPI(organizationId, selectedDocument.id, reasonId);
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudo evaluar la refacturación.');
            setPreview(getResponseData(response));
        } catch (error: any) { message.error(error?.message || 'No se pudo evaluar la refacturación.'); }
        finally { setRefactoringLoading(false); }
    };

    const executeRefactoring = async () => {
        if (!selectedDocument || !selectedReasonId || !preview || !paymentDisposition) return;
        const selectedReason = reasons.find(x => x.id === selectedReasonId);
        if (selectedReason?.requiresObservation && !observation.trim()) {
            message.warning('Debe ingresar una observación para este motivo.');
            return;
        }
        const userId = String(profile?.userId || profile?.id || profile?.Id || '');
        if (!userId) { message.error('No se pudo identificar al usuario responsable.'); return; }
        setRefactoringLoading(true);
        try {
            const response = await postDocumentRefactoringAPI(organizationId, selectedDocument.id, {
                organizationReasonId: selectedReasonId,
                paymentDisposition,
                observation: observation.trim() || null,
                msIdentityUserId: userId,
                msIdentityUserName: profile?.fullName || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || 'Usuario',
                idempotencyKey: createIdempotencyKey(),
                replacementSalesDocumentId: paymentDisposition === 'REASIGNACION' ? replacementSalesDocumentId : null
            });
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudo completar la operación.');
            const result = getResponseData(response);
            message.success(result?.message || 'Refacturación registrada.');
            setSelectedDocument(null);
            await load(filters, page);
        } catch (error: any) { message.error(error?.message || 'No se pudo completar la operación.'); }
        finally { setRefactoringLoading(false); }
    };

    const handleSearch = (values: SalesDocumentFilters) => {
        const normalized = Object.fromEntries(
            Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== '')
        ) as SalesDocumentFilters;
        setFilters(normalized);
        setPage(1);
        void load(normalized, 1);
    };

    const handleReset = () => {
        form.resetFields();
        setFilters({});
        setPage(1);
        void load({}, 1);
    };

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        void load(filters, nextPage);
    };

    return (
        <Card
            title="Facturas generadas"
            extra={(
                <Button icon={<ReloadOutlined />} onClick={() => void load(filters, page)} loading={loading}>
                    Actualizar
                </Button>
            )}
        >
            <Alert
                type="info"
                showIcon
                message="Historial de documentos de venta"
                description={runtimeEnvironment
                    ? `Ambiente activo: ${runtimeEnvironment.label}. Aquí se muestran las facturas registradas en este ambiente.`
                    : 'Aquí se muestran las facturas registradas en el ambiente activo.'}
                style={{ marginBottom: 20 }}
            />

            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 20 }} />}

            <Form form={form} layout="vertical" onFinish={handleSearch}>
                <Row gutter={16} align="bottom">
                    <Col xs={24} md={8}>
                        <Form.Item name="search" label="Buscar">
                            <Input prefix={<SearchOutlined />} placeholder="Número, identificación o cliente" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item name="status" label="Estado">
                            <Select allowClear options={[{ value: 'PAGADO' }, { value: 'PENDIENTE' }, { value: 'ANULADO' }]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item name="billingMode" label="Tipo de emisión">
                            <Select allowClear options={[{ value: 'DIRECTA', label: 'Directa' }, { value: 'TURNO', label: 'Turno' }]} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="fromDate" label="Desde">
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="toDate" label="Hasta">
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={2}>
                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" icon={<FileSearchOutlined />}>Buscar</Button>
                                <Button onClick={handleReset}>Limpiar</Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {loading ? <Spin /> : (
                <Table<SalesDocumentListItem>
                    rowKey="id"
                    columns={columns}
                    dataSource={list.items}
                    locale={{ emptyText: 'No hay facturas registradas.' }}
                    pagination={{
                        current: page,
                        pageSize: list.pageSize,
                        total: list.totalCount,
                        showSizeChanger: false,
                        showTotal: total => `${total} factura${total === 1 ? '' : 's'}`,
                        onChange: handlePageChange
                    }}
                    scroll={{ x: 900 }}
                />
            )}

            <Modal
                open={Boolean(selectedDocument)}
                title={`Anular o refacturar ${selectedDocument?.documentSeries || ''}-${selectedDocument?.documentNumber || ''}`}
                onCancel={() => setSelectedDocument(null)}
                onOk={() => void executeRefactoring()}
                okText={preview?.method === 'ANULACION' ? 'Confirmar anulación' : 'Generar nota de crédito'}
                okButtonProps={{ danger: preview?.method === 'ANULACION', disabled: !preview || !paymentDisposition || (paymentDisposition === 'REASIGNACION' && !replacementSalesDocumentId) }}
                confirmLoading={refactoringLoading}
                destroyOnClose
            >
                <Alert
                    type="warning"
                    showIcon
                    message="El sistema decide el documento fiscal aplicable"
                    description="Según la fecha de emisión y la regla legal vigente se realizará una anulación o una nota de crédito. El operador no escoge el método."
                    style={{ marginBottom: 16 }}
                />
                <Form layout="vertical">
                    <Form.Item label="Motivo autorizado" required>
                        <Select
                            value={selectedReasonId}
                            loading={refactoringLoading}
                            onChange={value => void loadPreview(value)}
                            options={reasons.map(reason => ({ value: reason.id, label: reason.customDescription || reason.description }))}
                            placeholder={reasons.length ? 'Seleccione un motivo' : 'El administrador debe habilitar motivos primero'}
                        />
                    </Form.Item>
                    {preview && (
                        <Alert
                            type={preview.method === 'ANULACION' ? 'warning' : 'info'}
                            showIcon
                            message={preview.method === 'ANULACION' ? 'Se aplicará anulación' : 'Se generará nota de crédito'}
                            description={`${preview.notice} Pago registrado: ${formatMoney(preview.paidAmount)}.`}
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    {preview && <Form.Item label="Tratamiento del pago" required>
                        <Select
                            value={paymentDisposition}
                            onChange={setPaymentDisposition}
                            options={preview.paymentDispositionOptions.map(value => ({
                                value,
                                label: value === 'DEVOLUCION' ? 'Devolución de dinero (queda pendiente de confirmar)' : value === 'SALDO_A_FAVOR' ? 'Saldo a favor del cliente' : value === 'REASIGNACION' ? 'Reasignar a factura reemplazo' : 'Sin pago'
                            }))}
                        />
                    </Form.Item>}
                    {paymentDisposition === 'REASIGNACION' && <Form.Item label="Factura reemplazo" required>
                        <Select
                            value={replacementSalesDocumentId}
                            onChange={setReplacementSalesDocumentId}
                            placeholder="Seleccione la factura que recibirá el pago"
                            options={list.items.filter(item => item.id !== selectedDocument?.id && item.status !== 'ANULADO').map(item => ({
                                value: item.id,
                                label: `${item.documentSeries}-${item.documentNumber} · ${item.clientName} · ${formatMoney(item.total)}`
                            }))}
                        />
                    </Form.Item>}
                    <Form.Item label="Observación">
                        <Input.TextArea value={observation} onChange={event => setObservation(event.target.value)} rows={3} maxLength={1000} />
                    </Form.Item>
                </Form>
            </Modal>

        </Card>
    );
}

export default SalesDocumentsManagement;
